"use client";

import { useSupabaseClient } from "@/components/SupabaseProvider";
import { useQuery } from "@tanstack/react-query";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { useMemo } from "react";
import { klap } from "../utils/klap";

// While any short in the folder is still materializing — `generating` (a manual
// exact-range short) or `processing` (a draft being generated) — poll so its
// placeholder card flips to the finished short (or an error card) without a
// manual refresh. Drafts are static, so they never trigger polling.
const GENERATING_POLL_INTERVAL_MS = 5000;

// In-flight shorts: a manual exact-range short being built (`generating`) or a
// draft being materialized via /projects/:id/generate (`processing`).
const isInFlight = (video: VideoModel) =>
  video.status === "generating" || video.status === "processing";

// List ordering tiers (lower = higher in the list):
//   0. Author-generated ("Generated by you") shorts — newest-first by
//      `created_at`, regardless of status. Sorting on `created_at` (set at
//      curation/creation, never on materialization) keeps a short in the same
//      slot as it moves draft → processing/generating → ready, so nothing
//      jumps around mid-generation. Includes prompt-guided "Create shorts"
//      clips (they carry both flags).
//   1. Guided batch clips (curation prompt badge): the user explicitly asked
//      for these moments on submission, so they group above the regular
//      batch — virality order within the group.
//   2. The original video-to-shorts batch — virality-score order (in-flight
//      rows from that batch keep their curation score, so they stay put too).
const orderRank = (video: VideoModel) =>
  video.generatedByAuthor ? 0 : video.curationPrompt != null ? 1 : 2;

const orderVideos = (videos: VideoModel[]): VideoModel[] =>
  [...videos].sort((a, b) => {
    const rankDelta = orderRank(a) - orderRank(b);
    if (rankDelta !== 0) return rankDelta;
    if (orderRank(a) >= 1)
      return (b.viralityScore ?? -Infinity) - (a.viralityScore ?? -Infinity);
    return b.createdAt.getTime() - a.createdAt.getTime();
  });

// Drafts (curated-but-not-generated moments): guided (curation prompt) drafts
// group on top like in the shorts list, then highest virality first (the
// curation order), newest first as a tiebreak.
const orderDrafts = (drafts: VideoModel[]): VideoModel[] =>
  [...drafts].sort(
    (a, b) =>
      (a.curationPrompt != null ? 0 : 1) -
        (b.curationPrompt != null ? 0 : 1) ||
      (b.viralityScore ?? -Infinity) - (a.viralityScore ?? -Infinity) ||
      b.createdAt.getTime() - a.createdAt.getTime(),
  );

export const folderVideosQueryKey = (folderId?: string) =>
  ["folder-videos", folderId] as const;

/** Every row of the folder (shorts + drafts), as `useFolder` caches it —
 *  exported so a host can prime the same cache entry imperatively (the
 *  schedule dialog's project switch resolves a folder's entry short before
 *  remounting on it). */
export const fetchFolderVideos = async (
  supabaseClient: ReturnType<typeof useSupabaseClient>,
  folderId: string,
): Promise<VideoModel[]> => {
  const { data, error } = await supabaseClient
    .from("videos_v2")
    .select("*")
    .eq("folder_id", folderId);
  if (error) throw error;
  return data.map(klap.video.fromRow);
};

export const useFolder = (folderId?: string) => {
  const supabaseClient = useSupabaseClient();

  // 1. Fetch every row in the folder (shorts + drafts) in one query.
  const {
    data: rows,
    isLoading: isLoadingVideos,
    error: videosError,
    refetch,
  } = useQuery({
    queryKey: folderVideosQueryKey(folderId),
    queryFn: async () => {
      if (!folderId) return [] as VideoModel[];
      return fetchFolderVideos(supabaseClient, folderId);
    },
    enabled: !!folderId,
    refetchInterval: (query) =>
      query.state.data?.some(isInFlight) ? GENERATING_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  // 2. Split drafts (curated moments) out of the shorts feed. Existing
  // consumers (publication flows, the shorts grid) only ever want real shorts;
  // the generate dialog + folder page consume `drafts` separately so an
  // un-generated draft never leaks in as a publishable/finished short.
  const videos = useMemo(
    () => orderVideos((rows ?? []).filter((video) => video.status !== "draft")),
    [rows],
  );
  const drafts = useMemo(
    () => orderDrafts((rows ?? []).filter((video) => video.status === "draft")),
    [rows],
  );

  return {
    videos,
    drafts,
    isLoading: isLoadingVideos,
    error: videosError,
    refetch,
  };
};
